import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TenantService } from '../../services/tenant.service';
import {
  faList,
  faSliders,
  faQuestion,
  faDownload,
  faUpload,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

import { ModelsFormComponent } from '../../components/models-form/models-form.component';
import { ClassificationConfigComponent } from '../../components/classification-config/classification-config.component';
import { ModelImportExportService } from '../../services/model-import-export.service';
import { TabsComponent, TabItem } from '../../components/tabs/tabs.component';
import { ModelsService, Model, ClassificationItem } from '../../services/models.service';
import { ProcessingsService, ProcessingOperation, Catalog } from '../../services/processings.service';
import { ProcessingOperationsComponent } from '../../components/processing-operations/processing-operations.component';
import { ProcessingCatalogComponent } from '../../components/processing-catalog/processing-catalog.component';
import { AlertService } from '../../components/alert-service/alert-service';
import { ModalHelpComponent } from '../../components/modal-help/modal-help.component';

type ModelKind = 'extraction' | 'classification' | 'processing';

@Component({
  selector: 'app-models-edit',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    ModelsFormComponent,
    ProcessingOperationsComponent,
    ProcessingCatalogComponent,
    ClassificationConfigComponent,
    TabsComponent,
    ModalHelpComponent
],
  templateUrl: './models-edit.component.html',
  styleUrls: ['./models-edit.component.scss']
})
export class ModelsEditComponent implements OnInit {
  loading = false;
  saving = false;
  isEditing = false;
  editingModelId: string | null = null;
  mentionData: string[] = [];
  currentFullModel: Model | null = null;
  modelType: ModelKind = 'extraction';

  activeTab: string = 'basic';

  get computedTabs(): TabItem[] {
    if (this.modelType === 'processing') {
      return [
        { id: 'operations', label: this.translate.instant('PROCESSINGS.TABS.OPERATIONS'), icon: faSliders },
        { id: 'catalog', label: this.translate.instant('PROCESSINGS.TABS.CATALOG'), icon: faList },
      ];
    }
    if (this.modelType === 'classification') {
      return [
        { id: 'classification', label: this.translate.instant('MODELS.TABS.CLASSIFICATION'), icon: faList }
      ];
    }
    return [
      { id: 'basic', label: this.translate.instant('MODELS.TABS.BASIC'), icon: faList, showHelp: true },
      { id: 'rules', label: this.translate.instant('MODELS.TABS.RULES'), icon: faSliders, showHelp: true },
      { id: 'questions', label: this.translate.instant('MODELS.TABS.QUESTIONS'), icon: faQuestion, showHelp: true },
    ];
  }

  showHelpModal = false;
  helpModalTitle = '';
  helpModalContent = '';

  modelForm: {
    name: string;
    version: string;
  } = {
    name: '',
    version: '2'
  };

  basicUserListInitial: any = null;
  generalRulesInitial: any = null;
  generalQuestionsInitial: any = null;
  classificationInitial: ClassificationItem[] = [];

  basicUserList: any = null;
  generalRulesData: any = null;
  generalQuestionsData: any = null;
  classificationData: ClassificationItem[] = [];

  formKey: number = 0;

  operations: ProcessingOperation[] = [];
  catalogData: Catalog | null = null;
  catalogError: string | null = null;

  faArrowLeft = faArrowLeft;
  faUpload = faUpload;
  faDownload = faDownload;

  constructor(
    private service: ModelsService,
    private translate: TranslateService,
    private alert: AlertService,
    private route: ActivatedRoute,
    private router: Router,
    private importExportService: ModelImportExportService,
    private processingsService: ProcessingsService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.enterEditMode(id);
      } else {
        this.enterCreateMode();
      }
    });
  }

  private enterCreateMode(): void {
    this.isEditing = false;
    this.editingModelId = null;
    this.modelType = 'extraction';
    this.activeTab = 'basic';
    this.modelForm = { name: '', version: '2' };
    this.basicUserListInitial = null;
    this.generalRulesInitial = null;
    this.generalQuestionsInitial = null;
    this.classificationInitial = [];
    this.basicUserList = null;
    this.generalRulesData = null;
    this.generalQuestionsData = null;
    this.classificationData = [];
    this.currentFullModel = null;
    this.formKey = Date.now();
  }

  onModelTypeChange(): void {
    this.activeTab = this.defaultTabFor(this.modelType);
    if (this.modelType === 'processing' && !this.catalogData) {
      this.loadCatalog();
    }
  }

  private defaultTabFor(modelType: string): string {
    if (modelType === 'processing') return 'operations';
    if (modelType === 'classification') return 'classification';
    return 'basic';
  }

  onOperationsChange(operations: ProcessingOperation[]): void {
    this.operations = operations;
  }

  private loadCatalog(): void {
    this.catalogError = null;
    this.processingsService.catalog().subscribe({
      next: catalog => { this.catalogData = catalog; this.catalogError = null; },
      error: err => {
        this.catalogData = null;
        this.catalogError = err?.error?.detail || err?.message || 'erro';
      },
    });
  }

  onCatalogRetry(): void {
    this.loadCatalog();
  }

  private enterEditMode(id: string): void {
    this.isEditing = true;
    this.editingModelId = id;
    this.activeTab = 'basic';
    this.loadModel(id);
  }

  private loadModel(id: string): void {
    this.loading = true;

    this.service.getById(id).subscribe({
      next: (fullModel) => {
        this.currentFullModel = fullModel;
        this.editingModelId = fullModel.id ? String(fullModel.id) : id;
        this.modelType = (fullModel.model_type as ModelKind) || 'extraction';
        this.activeTab = this.defaultTabFor(this.modelType);
        this.modelForm = {
          name: fullModel.name || '',
          version: fullModel.version || '2'
        };

        if (this.modelType === 'processing') {
          this.operations = JSON.parse(JSON.stringify(fullModel.operations || []));
          this.classificationInitial = [];
          this.basicUserListInitial = null;
          this.generalRulesInitial = null;
          this.generalQuestionsInitial = null;
          this.loadCatalog();
        } else if (this.modelType === 'classification') {
          this.classificationInitial = (fullModel.user_list as ClassificationItem[]) || [];
          this.classificationData = this.classificationInitial;
          this.basicUserListInitial = null;
          this.generalRulesInitial = null;
          this.generalQuestionsInitial = null;
        } else {
          this.basicUserListInitial =
            this.importExportService.mapUserListToFormStructure(
              fullModel.user_list || []
            );
          this.generalRulesInitial =
            this.importExportService.mapRulesToFormStructure(
              fullModel.general_rules || []
            );
          this.generalQuestionsInitial =
            this.importExportService.mapQuestionsToFormStructure(
              fullModel.general_questions || []
            );
          this.basicUserList = this.basicUserListInitial;
          this.generalRulesData = this.generalRulesInitial;
          this.generalQuestionsData = this.generalQuestionsInitial;
        }

        this.loading = false;
        this.formKey = Date.now();
      },
      error: (err) => {
        this.loading = false;
        this.alert.showError(err);
        this.goBack();
      }
    });
  }

  importModel(): void {
    if (!this.canImportModel) return;
    this.importExportService
      .importFromFile()
      .then((imported) => {
        this.modelForm = {
          name: imported.name,
          version: imported.version,
        };
        this.modelType = imported.model_type;

        if (imported.model_type === 'classification') {
          this.classificationInitial = imported.user_list;
          this.classificationData = [...this.classificationInitial];
          this.basicUserListInitial = null;
          this.generalRulesInitial = null;
          this.generalQuestionsInitial = null;
          this.basicUserList = null;
          this.generalRulesData = null;
          this.generalQuestionsData = null;
          this.activeTab = 'classification';
        } else {
          this.classificationInitial = [];
          this.classificationData = [];
          this.basicUserListInitial =
            this.importExportService.mapUserListToFormStructure(
              imported.user_list
            );
          this.generalRulesInitial =
            this.importExportService.mapRulesToFormStructure(
              imported.general_rules
            );
          this.generalQuestionsInitial =
            this.importExportService.mapQuestionsToFormStructure(
              imported.general_questions
            );
          this.basicUserList = this.basicUserListInitial;
          this.generalRulesData = this.generalRulesInitial;
          this.generalQuestionsData = this.generalQuestionsInitial;
          this.activeTab = 'basic';
        }

        this.formKey = Date.now();
        this.currentFullModel = null;
        this.alert.showSuccess(
          this.translate.instant('MODELS.IMPORT.SUCCESS')
        );
      })
      .catch((err) => {
        if (err) {
          this.alert.showError(err);
        }
      });
  }

  get canImportModel(): boolean {
    return !this.isEditing;
  }

  exportModel(): void {
    if (!this.currentFullModel) {
      this.alert.showError(
        this.translate.instant('MODELS.ERROR.NO_DATA_TO_EXPORT')
      );
      return;
    }
    try {
      this.importExportService.exportToFile(this.currentFullModel);
      this.alert.showSuccess(
        this.translate.instant('MODELS.EXPORT.SUCCESS')
      );
    } catch (err) {
      this.alert.showError(err);
    }
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/models'));
  }

  private persist(payload: Model): void {
    this.saving = true;
    const request = this.isEditing && this.editingModelId
      ? this.service.update(this.editingModelId, payload)
      : this.service.create(payload);
    const message = this.isEditing ? 'MODELS.UPDATE.SUCCESS' : 'MODELS.CREATE.SUCCESS';

    request.subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant(message));
        this.goBack();
      },
      error: (err) => {
        this.saving = false;
        this.alert.showError(err);
      },
    });
  }

  saveModel(): void {
    if (!this.modelForm.name?.trim()) {
      this.alert.showError(this.translate.instant('MODELS.ERROR.NAME_REQUIRED'));
      return;
    }

    if (this.modelType === 'processing') {
      this.persist({
        name: this.modelForm.name.trim(),
        version: '2',
        model_type: 'processing',
        operations: this.operations,
      });
      return;
    }

    const payload: Model = {
      name: this.modelForm.name.trim(),
      version: '2',
      model_type: this.modelType,
      user_list: this.modelType === 'classification'
        ? this.classificationData
        : this.importExportService.normalizeUserList(this.basicUserList),
      general_rules: this.modelType === 'classification'
        ? []
        : this.importExportService.normalizeGeneralRules(this.generalRulesData),
      general_questions: this.modelType === 'classification'
        ? []
        : this.importExportService.normalizeGeneralQuestions(this.generalQuestionsData)
    };

    this.persist(payload);
  }

  onMentionDataChange(ids: string[]): void {
    this.mentionData = ids;
  }

  get canSaveModel(): boolean {
    return !!this.modelForm.name?.trim();
  }

  onBasicFormChange(value: any): void {
    this.basicUserList = value;
  }

  onRulesFormChange(value: any): void {
    this.generalRulesData = value;
  }

  onQuestionsFormChange(value: any): void {
    this.generalQuestionsData = value;
  }

  onClassificationFormChange(value: ClassificationItem[]): void {
    this.classificationData = value;
  }

  onHelpClicked(tabId: string): void {
    const helpMap: Record<string, { title: string; content: string }> = {
      'basic': {
        title: this.translate.instant('MODELS.TABS.BASIC'),
        content: this.translate.instant('INTERACTION_MODEL_EDITOR.BASIC')
      },
      'rules': {
        title: this.translate.instant('MODELS.TABS.RULES'),
        content: this.translate.instant('INTERACTION_MODEL_EDITOR.GENERAL_RULES')
      },
      'questions': {
        title: this.translate.instant('MODELS.TABS.QUESTIONS'),
        content: this.translate.instant('INTERACTION_MODEL_EDITOR.GENERAL_QUESTIONS')
      }
    };

    const help = helpMap[tabId];
    if (help) {
      this.helpModalTitle = help.title;
      this.helpModalContent = help.content;
      this.showHelpModal = true;
    }
  }

  closeHelpModal(): void {
    this.showHelpModal = false;
  }
}
